import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import { ACTIONS_COLUMN_ID, type BodyColumnDef } from "@src/components/ui-kit";
import { DataTable } from "@src/components/ui-kit/DataTable";
import { LoadingOverlay } from "@src/components/ui-kit/LoadingOverlay/LoadingOverlay";
import { Pagination } from "@src/components/ui-kit/table/pagination/Pagination";
import { currency, formatDate } from "@src/utils/formatters";
import { observer } from "mobx-react-lite";
import { useMemo } from "react";
import type { RelatedDocumentsEditModalStore } from "./store";

type Props = {
  readonly store: RelatedDocumentsEditModalStore;
};

// Get the types from the store
type ExpenseItem = NonNullable<
  RelatedDocumentsEditModalStore extends { displayedExpenses: infer T }
    ? T
    : never
>[number];

export const RelatedDocumentsEditModal = observer(
  function RelatedDocumentsEditModal({ store }: Props) {
    const expenseColumns = useMemo<BodyColumnDef<ExpenseItem>[]>(
      () => [
        {
          id: "expense_no",
          meta: { label: t`Expense No` },
          header: () => t`Expense No`,
          cell: ({ row }) => row.original.expense_no ?? row.original.id,
        },
        {
          id: "contractor",
          meta: { label: t`Contractor` },
          header: () => t`Contractor`,
          cell: ({ row }) => row.original.contractor?.legal_name ?? "-",
        },
        {
          id: "date_of_supply",
          meta: { label: t`Date` },
          header: () => t`Date`,
          cell: ({ row }) =>
            row.original.date_of_supply
              ? formatDate(new Date(row.original.date_of_supply))
              : "-",
        },
        {
          id: "amount",
          meta: { label: t`Amount`, align: "right" },
          header: () => t`Amount`,
          cell: ({ row }) =>
            currency.formatByWorkspace(
              row.original.stats?.total_amount_with_vat_in_currency ?? 0,
            ),
        },
        {
          id: ACTIONS_COLUMN_ID,
          meta: { label: t`Actions` },
        },
      ],
      [],
    );

    return (
      <Modal
        isOpen={store.disclosureState.isOpen}
        onClose={store.disclosureState.onClose}
        size="3xl"
        trapFocus={false}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Trans>Edit Related Documents</Trans>
          </ModalHeader>

          <ModalCloseButton />

          <ModalBody>
            <DataTable>
              <LoadingOverlay isLoading={store.isLoading} position="absolute">
                <DataTable.Body
                  tableKey={store.key}
                  data={store.displayedExpenses}
                  columns={expenseColumns}
                  enableRowSelection
                  selected={store.selectedExpenseIds}
                  onSelect={store.setSelectedExpenseIds}
                />
              </LoadingOverlay>

              <Pagination store={store.pagination} />
            </DataTable>
          </ModalBody>

          <ModalFooter>
            <Button onClick={store.disclosureState.onClose} variant="ghost">
              <Trans>Cancel</Trans>
            </Button>
            <Button ml="2" onClick={store.handleConfirm}>
              <Trans>Confirm</Trans>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  },
);
